import { useMemo } from "react";
import { useRecipe } from "@/hooks/recipes";
import { useAllRecipeIngredients, useRecipeStages } from "@/hooks/recipes";
import { buildStagedMethod } from "@/lib/recipes/staged-method";
import type { RecipeStep } from "@/components/recipes/execution-steps";
import type { Recipe } from "@/types/recipe";

/**
 * A session's recipe laid out by stage: the stages, every ingredient line and
 * the steps, joined the way the recipe card does. The queries are the ones the
 * recipe viewer already runs, so a recipe on screen twice reads the cache.
 */
export function useSessionMethod(recipeId: number) {
  const recipeQuery = useRecipe(recipeId);
  const stagesQuery = useRecipeStages(recipeId);
  const linesQuery = useAllRecipeIngredients(recipeId);
  const recipe = recipeQuery.data as Recipe | undefined;
  const stages = stagesQuery.data;
  const lines = linesQuery.data?.data;

  const method = useMemo(() => {
    if (!recipe || !stages || !lines) return null;
    const steps: RecipeStep[] = Array.isArray(recipe.instructions) ? (recipe.instructions as RecipeStep[]) : [];
    return buildStagedMethod(stages, lines, steps);
  }, [recipe, stages, lines]);

  return {
    recipe,
    method,
    isLoading: recipeQuery.isLoading || stagesQuery.isLoading || linesQuery.isLoading,
    isError: recipeQuery.isError || stagesQuery.isError || linesQuery.isError,
    retry: () => {
      if (recipeQuery.isError) void recipeQuery.refetch();
      if (stagesQuery.isError) void stagesQuery.refetch();
      if (linesQuery.isError) void linesQuery.refetch();
    },
  };
}
